import _defineProperty from "@babel/runtime/helpers/defineProperty";
import { Callback1Arg } from "abweb/utils/callback/Callback1Arg";
import { LogUtils } from "abweb/utils/log/LogUtils";
import { DataGridColumn } from "./DataGridColumn";
import { SelectionRegion } from "./SelectionRegion";

/**
 * Selection machine that works with the DataGrid component.
 * This machine allows you to control selection programmatically, and get notifications when
 * the user changes the selection interactively.
 */
export class DataGridSelectionMachine {
  constructor() {
    /**
     * Notification that the selection has changed.
     */
    _defineProperty(this, "onSelectionChange", void 0);
    /**
     * Notification that the focus cell has changed.
     */
    _defineProperty(this, "onFocusCellChange", void 0);
    /**
     * Implementation method for call to getSelectionRange().
     * If you want getSelectionRange() to work, you must supply an implementation for it
     * by specifying this method.
     * The selectedItems passed in will be null if nothing is selected. You should return null in this case.
     */
    _defineProperty(this, "getSelectionRangeImpl", void 0);
    _defineProperty(this, "onSelectionChangePrivateCallback", void 0);
    _defineProperty(this, "onFocusCellChangePrivateCallback", void 0);
    _defineProperty(this, "selectionRegion", new SelectionRegion());
    _defineProperty(this, "_selectionMode", SelectionMode.None);
    _defineProperty(this, "selectionChangeListeners", new Callback1Arg());
    this.selectionRegion.onSelectionChange = this.onSelectionChangeInternal.bind(this);
    this.selectionRegion.onFocusCellChange = this.onFocusCellChangeInternal.bind(this);
  }
  get selectionMode() {
    return this._selectionMode;
  }
  set selectionMode(value) {
    if (value === this.selectionMode) {
      return;
    }
    this._selectionMode = value;
    this.clear();
  }
  get isSingleSelectionMode() {
    return this.selectionMode === SelectionMode.SingleRow || this.selectionMode === SelectionMode.SingleCell;
  }
  get isMultipleSelectionMode() {
    return this.selectionMode === SelectionMode.MultipleRows || this.selectionMode === SelectionMode.MultipleCells || this.selectionMode === SelectionMode.RectangularCells;
  }
  get isRectangularSelectionMode() {
    return this.selectionMode === SelectionMode.RectangularCells;
  }
  get isRowSelectionMode() {
    return this.selectionMode === SelectionMode.SingleRow || this.selectionMode === SelectionMode.MultipleRows;
  }
  get isCellSelectionMode() {
    return this.canSelectCells;
  }
  get canSelect() {
    return this.selectionMode !== SelectionMode.None;
  }
  get canSelectCells() {
    return this.selectionMode === SelectionMode.MultipleCells || this.selectionMode === SelectionMode.SingleCell || this.selectionMode === SelectionMode.RectangularCells;
  }
  get canSelectColumns() {
    return this.selectionMode === SelectionMode.MultipleCells || this.selectionMode === SelectionMode.RectangularCells;
  }

  /**
   * Clears the selection region.
   */
  clear() {
    return this.selectionRegion.clear();
  }

  /**
   * Selects a row.
   * @param {RowId} rowId
   * @param {boolean} addToSelection if true, the row is added to the selection, if false, any current selection is cleared.
   * @param {boolean | undefined} scrollInfoView if true, the row is scrolled in view
   * Note: for you need to have DataGrid ref for scrollInfoView to work  i.e. <DataGrid ref={(x) => this.gridSelectionMachine.grid = x} ...
   */
  selectRow(rowId, addToSelection, scrollInfoView) {
    if (!this.canSelect) {
      return;
    }
    if (rowId == null) {
      return;
    }
    this.selectionRegion.suspendChangeNotifications();
    if ((this.isSingleSelectionMode || !addToSelection) && !this.selectionRegion.isOnlyItemSelected(rowId, null)) {
      this.clear();
    }
    this.selectionRegion.clearFocusCell();
    this.selectionRegion.anchorItem2 = new SelectedItem(rowId);
    if (!addToSelection || this.selectionRegion.anchorItem == null) {
      this.selectionRegion.anchorItem = this.selectionRegion.anchorItem2;
    }
    this.selectionRegion.selectRow(rowId);
    this.selectionRegion.resumeChangeNotifications();
    if (scrollInfoView && this.grid) {
      this.grid.scrollToCell({
        rowId
      });
    }
  }

  /**
   * Selects the indicated item.
   * @param {SelectedItem} item Item to select
   * @param {boolean} addToSelection if true, the item is added to the selection,
   * if false, any current selection is cleared.
   */
  selectItem(item, addToSelection) {
    if (item.isRow) {
      this.selectRow(item.rowId, addToSelection);
    } else if (item.isCell) {
      this.selectCell(item.rowId, item.colId, addToSelection, true);
    } else {
      this.selectColumn(item.colId, addToSelection);
    }
  }

  /**
   * Deselects a row.
   * @param {RowId} rowId
   */
  deselectRow(rowId) {
    this.selectionRegion.deselectRow(rowId);
  }

  /**
   * User action when clicked on a selected row with ctrlKey.
   * @param {RowId} rowId
   */
  onCtrlClickSelectedRow(rowId) {
    // By default ctrl-click deselects the row
    this.deselectRow(rowId);
  }

  /**
   * Selects a cell.
   * @param {RowId} rowId
   * @param {ColId} colId
   * @param {boolean} addToSelection if true, the row is added to the selection,
   * if false, any current selection is cleared.
   * @param {boolean} isFocusCell
   */
  selectCell(rowId, colId, addToSelection, isFocusCell) {
    if (!this.canSelect || this.isRowSelectionMode) {
      return;
    }
    if (rowId == null || colId == null) {
      return;
    }
    this.selectionRegion.suspendChangeNotifications();
    if ((this.isSingleSelectionMode || !addToSelection) && !this.selectionRegion.isOnlyItemSelected(rowId, colId)) {
      this.clear();
    }
    if (isFocusCell) {
      this.selectionRegion.setFocusCell(rowId, colId);
    }
    this.selectionRegion.selectCell(rowId, colId);
    this.selectionRegion.resumeChangeNotifications();
  }

  /**
   * Deselects a cell.
   * @param {RowId} rowId
   * @param {ColId} colId
   */
  deselectCell(rowId, colId) {
    this.selectionRegion.deselectCell(rowId, colId);
  }

  /**
   * User action when clicked on a selected cell with ctrlKey.
   * @param {RowId} rowId
   * @param {string} colId
   */
  onCtrlClickSelectedCell(rowId, colId) {
    // By default ctrl-click deselects the cell
    this.deselectCell(rowId, colId);
  }

  /**
   * Select a column.
   * @param {ColId} colId
   * @param {boolean} addToSelection addToSelection if true, the column is added to the selection,
   * if false, any current selection is cleared.
   */
  selectColumn(colId, addToSelection) {
    if (!this.canSelect || !this.canSelectColumns) {
      return;
    }
    if (colId == null) {
      return;
    }
    this.selectionRegion.suspendChangeNotifications();
    if ((this.isSingleSelectionMode || !addToSelection) && !this.selectionRegion.isOnlyItemSelected(null, colId)) {
      this.clear();
    }
    this.selectionRegion.clearFocusCell();
    this.setAnchorItemsForColumnSelection(colId, addToSelection);
    this.selectionRegion.selectColumn(colId);
    this.selectAssociatedGroupedColumns(colId);
    this.selectionRegion.resumeChangeNotifications();
  }

  /**
   * Called from selectColumn() to select any associated grouped columns.
   * @param {string} colId
   */
  selectAssociatedGroupedColumns(colId) {
    let col = this.dataGrid.getColumnFromId(colId);
    if (col == null) {
      return;
    }

    // Select all child columns
    if (col.childColumns) {
      for (let childCol of col.childColumns) {
        if (!this.isColumnSelected(childCol.id)) {
          this.selectColumn(childCol.id, true);
        }
      }
    }

    // Check all siblings. If we are all selected, select my parent
    let parentCol = col.parentColumn;
    if (parentCol == null) {
      return;
    }
    for (let childCol of parentCol.childColumns) {
      if (!this.isColumnSelected(childCol.id)) {
        // Not all siblings are selected so punt
        return;
      }
    }

    // If we get here, we can select the parentCol too
    this.selectColumn(parentCol.id, true);
  }

  /**
   * Called from deselectColumn() to deselect any associated grouped columns.
   * @param {string} colId
   */
  deselectAssociatedGroupedColumns(colId) {
    let col = this.dataGrid.getColumnFromId(colId);
    if (col == null) {
      return;
    }

    // Deselect all child columns
    if (col.childColumns) {
      for (let childCol of col.childColumns) {
        if (this.isColumnSelected(childCol.id)) {
          this.deselectColumn(childCol.id);
        }
      }
    }

    // Deselect my parents
    let parentCol = col.parentColumn;
    while (parentCol != null) {
      this.selectionRegion.deselectColumn(parentCol.id);
      parentCol = parentCol.parentColumn;
    }
  }
  setAnchorItemsForColumnSelection(colId, addToSelection) {
    let col = this.dataGrid.getColumnFromId(colId);
    if (col == null) {
      return;
    }
    if (!addToSelection) {
      this.selectionRegion.anchorItemGroup = col.isLeafColumn ? null : new SelectedItem(null, colId);
    }
    if (col.isLeafColumn) {
      this.selectionRegion.anchorItem2 = new SelectedItem(null, colId);
      if (!addToSelection || this.selectionRegion.anchorItem == null) {
        this.selectionRegion.anchorItem = this.selectionRegion.anchorItem2;
      }
      return;
    }
    let leafCols = col.getAllLeafColumns(true);
    let leftColId = leafCols[0].id;
    let rightColId = leafCols[leafCols.length - 1].id;
    if (!addToSelection || this.selectionRegion.anchorItem == null) {
      this.selectionRegion.anchorItem = new SelectedItem(null, leftColId);
      this.selectionRegion.anchorItem2 = new SelectedItem(null, rightColId);
    } else {
      this.selectionRegion.anchorItem2 = new SelectedItem(null, rightColId);
    }
  }

  /**
   * Deselects a column.
   * @param {ColId} colId
   */
  deselectColumn(colId) {
    this.selectionRegion.suspendChangeNotifications();
    this.selectionRegion.deselectColumn(colId);
    this.deselectAssociatedGroupedColumns(colId);
    this.selectionRegion.resumeChangeNotifications();
  }

  /**
   * User action when clicked on a selected column with ctrlKey.
   * @param {string} colId
   */
  onCtrlClickSelectedColumn(colId) {
    // By default we do nothing, which is the answer to WWED (What Would Excel Do).
  }

  /**
   * Determines if cell is selected either explicitly or via row or column selection.
   * @param {RowId} rowId
   * @param {ColId} colId
   * @returns {boolean}
   */
  isSelected(rowId, colId) {
    return this.selectionRegion.isSelected(rowId, colId);
  }

  /**
   * Determines if cell is selected explicitly as a selected cell.
   * @param {RowId} rowId
   * @param {ColId} colId
   * @returns {boolean}
   */
  isCellSelected(rowId, colId) {
    return this.selectionRegion.isCellSelected(rowId, colId);
  }
  isFocusCell(rowId, colId) {
    return this.selectionRegion.isFocusCell(rowId, colId);
  }
  getFocusCell() {
    return this.selectionRegion.focusCell;
  }
  isRowSelected(rowId) {
    return this.selectionRegion.isRowSelected(rowId);
  }
  isColumnSelected(colId) {
    return this.selectionRegion.isColumnSelected(colId);
  }
  columnHasSelectedCells(colId) {
    return this.selectionRegion.columnHasSelectedCells(colId);
  }
  get hasRowsSelected() {
    return this.selectionRegion.hasRowsSelected;
  }
  get selectedRowCount() {
    return this.selectionRegion.selectedRowCount;
  }
  get hasColumnsSelected() {
    return this.selectionRegion.hasColumnsSelected;
  }

  /**
   * Selects everything in the grid.
   */

  selectAll() {
    if (!this.isMultipleSelectionMode) {
      return;
    }
    this.selectionRegion.suspendChangeNotifications();
    this.selectionRegion.selectAll();
    this.selectionRegion.resumeChangeNotifications();
  }

  /**
   * Selects the indicated items.
   * @param {SelectedItem[]} selectedItems Items to select
   * @param {boolean} addToSelection if true, the items are added to the selection,
   * if false, any current selection is cleared.
   */
  selectMultipleItems(selectedItems, addToSelection) {
    if (!this.isMultipleSelectionMode) {
      return;
    }
    this.selectionRegion.suspendChangeNotifications();
    if (!addToSelection) {
      this.clear();
    }
    selectedItems.forEach(selectedItem => this.selectItem(selectedItem, true));
    this.selectionRegion.resumeChangeNotifications();
  }

  /**
   * Returns array of SelectedItems.
   * @returns {SelectedItem[]}
   */
  getSelectedItems() {
    let items = this.selectionRegion.getSelectedItems();

    // jira CAFE-3674 add this check
    // To keep tests happy, only selectAll and a sort of more than one item actually needs grid data.
    if (this.dataGrid == null && (this.selectionRegion.isSelectAll || items.length > 1)) {
      return [];
    }
    if (this.selectionRegion.isSelectAll) {
      // Special case that doesn't work above
      items = [];
      for (let rowIndex = 0; rowIndex < this.dataGrid.props.rowCount; rowIndex++) {
        let rowId = this.dataGrid.getRowIdFromIndex(rowIndex);
        items.push(new SelectedItem(rowId, null));
      }

      // already sorted by row
      return items;
    }

    // sort by row and column
    items.sort((a, b) => {
      if (a === b) {
        return 0;
      }
      if (a.rowId != null || b.rowId !== null) {
        if (a.rowId == null) {
          // a is a column, and b is either row or cell
          // since columns go last...
          return 1;
        } else if (b.rowId == null) {
          // b is a column, and a is either row or cell
          // since columns go last...
          return -1;
        }

        // both have a rowId
        let rowIndexa = this.dataGrid.getRowIndexFromId(a.rowId);
        let rowIndexb = this.dataGrid.getRowIndexFromId(b.rowId);
        if (rowIndexa === rowIndexb) {
          // Both are in same row
          // so sort by colIndex
          let colIndexa = a.colId == null ? -1 : this.dataGrid.getColumnIndexFromId(a.colId);
          let colIndexb = b.colId == null ? -1 : this.dataGrid.getColumnIndexFromId(b.colId);
          return colIndexa - colIndexb;
        } else {
          // sort by rowIndex
          return rowIndexa - rowIndexb;
        }
      }

      // both items are columns
      return this.dataGrid.getColumnIndexFromId(a.colId) - this.dataGrid.getColumnIndexFromId(b.colId);
    });
    return items;
  }

  /**
   * Returns a rectangular region of selected data item cells for the current selection region.
   * Note that to use this method you must supply a getSelectionChangeImpl implementation to convert the SelectedItem[]
   * into a SelectionRange.
   * @returns {SelectionRange | null | undefined} A SelectionRange object, or null if nothing is selected,
   * or undefined if no getSelectionChangeImpl has been supplied.
   */
  getSelectionRange() {
    if (this.getSelectionRangeImpl === undefined) {
      DataGridSelectionMachine.LOG.info("getSelectionRangeImpl has not been set. You must set this in order to use the getSelectionRange() method.");
      return undefined;
    }
    let selectedItems = this.isEmpty ? null : this.getSelectedItems();
    return this.getSelectionRangeImpl(selectedItems, this.dataGrid.props.columns, this);
  }
  get isSelectAll() {
    return this.selectionRegion.isSelectAll;
  }
  get isEmpty() {
    return this.selectionRegion.isEmpty;
  }

  /**
   * Determines whether or not the currently selected items are rectangular.
   * In order to be rectangular the cells must be either consecutive rows, consecutive columns, or a selection of
   * cells that forms a rectangle with no gaps.
   * @returns {boolean}
   */
  get isSelectionRectangular() {
    if (this.isSelectAll) {
      return true;
    }
    const selectedItems = this.getSelectedItems();
    if (selectedItems.length === 0) {
      return false;
    }
    const firstItem = selectedItems[0];
    const mustBeRow = firstItem.isRow;
    const mustBeColumn = firstItem.isColumn;
    let lastRow = mustBeColumn ? -1 : this.dataGrid.getRowIndexFromId(firstItem.rowId);
    let lastCol = mustBeRow ? -1 : this.dataGrid.getColumnIndexFromId(firstItem.colId);
    let leftCol = lastCol;
    let rightCol = -1;
    if (!mustBeRow && !mustBeColumn) {
      const finalItem = selectedItems[selectedItems.length - 1];
      if (finalItem.isRow || finalItem.isColumn) {
        return false;
      }
      rightCol = this.dataGrid.getColumnIndexFromId(finalItem.colId);
    }
    for (let i = 1; i < selectedItems.length; i++) {
      const item = selectedItems[i];
      if (mustBeRow && !item.isRow) {
        return false;
      } else if (mustBeColumn && !item.isColumn) {
        return false;
      } else if (!(mustBeColumn || mustBeRow) && (item.isColumn || item.isRow)) {
        return false;
      }
      if (mustBeRow) {
        const row = this.dataGrid.getRowIndexFromId(item.rowId);
        if (row !== lastRow + 1) {
          return false;
        }
        lastRow++;
      } else if (mustBeColumn) {
        const col = this.dataGrid.getColumnIndexFromId(item.colId);
        if (col !== lastCol + 1) {
          return false;
        }
        lastCol++;
      } else {
        // it's a cell and so it's either the next column or the next row
        const row = this.dataGrid.getRowIndexFromId(item.rowId);
        const col = this.dataGrid.getColumnIndexFromId(item.colId);
        if (lastCol === rightCol) {
          if (row !== lastRow + 1 || col !== leftCol) {
            return false;
          }
        } else {
          if (row !== lastRow || col !== lastCol + 1) {
            return false;
          }
        }
        lastRow = row;
        lastCol = col;
      }
    }
    return true;
  }

  /**
   * Returns true if a selected item currently exists in the grid
   */
  selectedItemExists(item) {
    return this.dataGrid.getRowIndexFromId(item.rowId) !== -1 || this.dataGrid.getColumnIndexFromId(item.colId) !== -1;
  }

  /**
   * Allows you to perform a callback on each selected cell
   * @param {(cell: {rowId: string | number; colId: string}) => void} cb
   */
  forEachSelectedCell(cb) {
    const selectedItems = this.getSelectedItems();
    const selectedCells = new Map();
    selectedItems.forEach(item => {
      // Selected item is a row
      if (item.isRowTypeGuard()) {
        const rowId = item.rowId;
        for (let col = 0; col <= this.dataGrid.getLeafColumnsCount() - 1; col++) {
          const colId = this.dataGrid.getColumnIdFromIndex(col);
          selectedCells.set(rowId + "." + colId, {
            rowId,
            colId
          });
        }
      }
      // Selected item is a column
      else if (item.isColumnTypeGuard()) {
        const colId = item.colId;
        for (let row = 0; row <= this.dataGrid.props.rowCount - 1; row++) {
          const rowId = this.dataGrid.getRowIdFromIndex(row);
          if (rowId != null) {
            selectedCells.set(rowId + "." + colId, {
              rowId,
              colId
            });
          }
        }
      }
      // Selected item is a cell
      else if (item.isCellTypeGuard()) {
        const {
          rowId,
          colId
        } = item;
        selectedCells.set(rowId + "." + colId, {
          rowId,
          colId
        });
      }
    });
    selectedCells.forEach(cell => {
      cb(cell);
    });
  }
  generateClipboardData(getString) {
    const rows = [];
    let curRow;
    let row = [];
    this.forEachSelectedCell(cell => {
      if (curRow === undefined) {
        curRow = cell.rowId;
      }
      if (cell.rowId === curRow) {
        row.push(getString(cell));
      } else {
        curRow = cell.rowId;
        rows.push(row);
        row = [getString(cell)];
      }
    });
    if (row.length !== 0) {
      rows.push(row);
    }
    return rows;
  }

  /**
   * Suspends future selection change notifications until the next call to resumeChangeNotifications().
   * You may use this if you want to make multiple selection change calls and treat them as a single atomic operation.
   * Make sure you always pair calls to suspendChangeNotifications()/resumeChangeNotifications(). If they are not paired
   * you may inadvertently suspend all future notifications.
   */
  suspendChangeNotifications() {
    this.selectionRegion.suspendChangeNotifications();
  }

  /**
   * Resumes change notifications.
   * If there are pending change notifications, they will fire now.
   * @returns {boolean} true if either onSelectionChange() or onFocusCellChange() has been called.
   */
  resumeChangeNotifications() {
    return this.selectionRegion.resumeChangeNotifications();
  }
  onSelectionChangeInternal() {
    // console.log(`onSelectionChangeInternal`);

    if (this.isRectangularSelectionMode) {
      if (!this.isEmpty && !this.isSelectionRectangular) {
        console.error("The Datagrid selection region was not rectangular, it has been cleared.");
        this.selectionRegion.clear();
        return;
      }
    }
    if (this.onSelectionChangePrivateCallback) {
      this.onSelectionChangePrivateCallback();
    }
    if (this.onSelectionChange) {
      this.onSelectionChange();
    }
    this.selectionChangeListeners.callAll(this);
  }
  onFocusCellChangeInternal() {
    if (this.onFocusCellChangePrivateCallback) {
      this.onFocusCellChangePrivateCallback();
    }
    if (this.onFocusCellChange) {
      this.onFocusCellChange();
    }
  }

  /**
   * Adds a selectionChange notifier.
   * Note that if you are confident that you are the only listener for selection changes, you can just
   * use the onSelectionChange() callback. But if there may be other objects that might want to be notified
   * of selection changes, you can add a listener here.
   * @param {(selectionMachine: DataGridSelectionMachine) => void} listener Callback to be called when the selection region changes.
   * @returns {IUnsubscriber} Unsubscriber you can use to remove the listener.
   * You can either call removeSelectionChangeListener(listener) with the same argument you used here,
   * or you can use the IUnsubscriber and call it.
   */
  addSelectionChangeListener(listener) {
    return this.selectionChangeListeners.addCallback(listener);
  }
  removeSelectionChangeListener(listener) {
    return this.selectionChangeListeners.removeCallback(listener);
  }

  /**
   * "Friend" method called from DataGridSelectionMgr used to extend row selection.
   * @param {RowId} rowId3
   */
  extendRowSelection(rowId3) {
    try {
      this.selectionRegion.suspendChangeNotifications();
      if (this.selectionRegion.anchorItem == null || !this.selectionRegion.anchorItem.isRow) {
        this.selectRow(rowId3, false);
        return;
      }

      // Have to consider 6 regions
      // TODO: Once we support hierarchical data, have to revisit to get a true index from rowId
      let index1 = this.dataGrid.getRowIndexFromId(this.selectionRegion.anchorItem.rowId);
      let index2 = this.dataGrid.getRowIndexFromId(this.selectionRegion.anchorItem2.rowId);
      let index3 = this.dataGrid.getRowIndexFromId(rowId3);
      let s1 = -1;
      let s2 = -1;
      let u1 = -1;
      let u2 = -1;
      if (index1 <= index2) {
        if (index3 >= index2) {
          // Simple extend
          s1 = index2 + 1;
          s2 = index3;
        } else if (index3 > index1) {
          u1 = index3 + 1;
          u2 = index2;
        } else {
          u1 = index1 + 1;
          u2 = index2;
          s1 = index3;
          s2 = index1 - 1;
        }
      } else {
        // Inverted region where index2 < index1
        if (index3 <= index2) {
          s1 = index3;
          s2 = index2 - 1;
        } else if (index3 < index1) {
          u1 = index2;
          u2 = index3 - 1;
        } else {
          u1 = index2;
          u2 = index1 - 1;
          s1 = index1 + 1;
          s2 = index3;
        }
      }
      if (s1 >= 0) {
        for (let n = s1; n <= s2; n++) {
          this.selectRow(this.dataGrid.getRowIdFromIndex(n), true);
        }
      }
      if (u1 >= 0) {
        for (let n = u1; n <= u2; n++) {
          this.deselectRow(this.dataGrid.getRowIdFromIndex(n));
        }
      }
      this.selectionRegion.anchorItem2 = new SelectedItem(rowId3);
    } finally {
      this.selectionRegion.resumeChangeNotifications();
    }
  }

  /**
   * "Friend" method called from DataGridSelectionMgr used to extend cell selection.
   * @param {RowId} rowId
   * @param {ColId} colId
   */
  extendCellSelection(rowId, colId) {
    try {
      this.selectionRegion.suspendChangeNotifications();
      if (this.selectionRegion.anchorItem == null || !this.selectionRegion.anchorItem.isCell || this.selectionRegion.anchorItem2 == null || !this.selectionRegion.anchorItem2.isCell) {
        this.selectCell(rowId, colId, false, true);
        return;
      }
      let item3 = new SelectedItem(rowId, colId);

      // Have to consider 6 row regions!
      const index1 = this.dataGrid.getRowIndexFromId(this.selectionRegion.anchorItem.rowId);
      const index2 = this.dataGrid.getRowIndexFromId(this.selectionRegion.anchorItem2.rowId);
      const index3 = this.dataGrid.getRowIndexFromId(item3.rowId);
      let s1 = -1;
      let s2 = -1;
      let u1 = -1;
      let u2 = -1;
      if (index1 <= index2) {
        if (index3 >= index2) {
          // Simple extend
          s1 = index2 + 1;
          s2 = index3;
        } else if (index3 > index1) {
          u1 = index3 + 1;
          u2 = index2;
        } else {
          u1 = index1 + 1;
          u2 = index2;
          s1 = index3;
          s2 = index1 - 1;
        }
      } else {
        // Inverted region where index2 < index1
        if (index3 <= index2) {
          s1 = index3;
          s2 = index2 - 1;
        } else if (index3 < index1) {
          u1 = index2;
          u2 = index3 - 1;
        } else {
          u1 = index2;
          u2 = index1 - 1;
          s1 = index1 + 1;
          s2 = index3;
        }
      }

      // and determine colIndex changes
      // This code naively assumes that all 3 cells are in same section (locked or unlocked)
      const cindex1 = this.dataGrid.getColumnIndexFromId(this.selectionRegion.anchorItem.colId);
      const cindex2 = this.dataGrid.getColumnIndexFromId(this.selectionRegion.anchorItem2.colId);
      const cindex3 = this.dataGrid.getColumnIndexFromId(item3.colId);
      let cs1 = -1;
      let cs2 = -1;
      let cu1 = -1;
      let cu2 = -1;
      if (cindex1 <= cindex2) {
        if (cindex3 >= cindex2) {
          // Simple extend
          cs1 = cindex2 + 1;
          cs2 = cindex3;
        } else if (cindex3 > cindex1) {
          cu1 = cindex3 + 1;
          cu2 = cindex2;
        } else {
          cu1 = cindex1 + 1;
          cu2 = cindex2;
          cs1 = cindex3;
          cs2 = cindex1 - 1;
        }
      } else {
        // Inverted region where index2 < index1
        if (cindex3 <= cindex2) {
          cs1 = cindex3;
          cs2 = cindex2 - 1;
        } else if (cindex3 < cindex1) {
          cu1 = cindex2;
          cu2 = cindex3 - 1;
        } else {
          cu1 = cindex2;
          cu2 = cindex1 - 1;
          cs1 = cindex1 + 1;
          cs2 = cindex3;
        }
      }

      // Now do processing
      if (s1 >= 0) {
        // We have new rows that need to be selected
        for (let n = s1; n <= s2; n++) {
          const newRowId = this.dataGrid.getRowIdFromIndex(n);
          const c1 = Math.min(cindex1, cindex3);
          const c2 = Math.max(cindex1, cindex3);
          for (let m = c1; m <= c2; m++) {
            // Note that m is an index into the grid._leafColumns Array
            let newColId = this.dataGrid.getColumnIdFromIndex(m);
            this.selectCell(newRowId, newColId, true, false);
          }
        }
      }
      if (u1 >= 0) {
        // We need to unselect some rows
        for (let n = u1; n <= u2; n++) {
          const newRowId = this.dataGrid.getRowIdFromIndex(n);
          const c1 = Math.min(cindex1, cindex2);
          const c2 = Math.max(cindex1, cindex2);
          for (let m = c1; m <= c2; m++) {
            let newColId = this.dataGrid.getColumnIdFromIndex(m);
            this.deselectCell(newRowId, newColId);
          }
        }
      }

      // Now process cell changes in the set of rows still there
      let n1 = Math.min(index1, index2);
      let n2 = Math.max(index1, index2);
      if (u1 >= 0) {
        if (u1 > n1) {
          n2 = u1 - 1;
        } else {
          n1 = u2 + 1;
        }
      }
      if (cs1 >= 0) {
        for (let n = n1; n <= n2; n++) {
          const newRowId = this.dataGrid.getRowIdFromIndex(n);
          for (let m = cs1; m <= cs2; m++) {
            let newColId = this.dataGrid.getColumnIdFromIndex(m);
            this.selectCell(newRowId, newColId, true, false);
          }
        }
      }
      if (cu1 >= 0) {
        for (let n = n1; n <= n2; n++) {
          const newRowId = this.dataGrid.getRowIdFromIndex(n);
          for (let m = cu1; m <= cu2; m++) {
            let newColId = this.dataGrid.getColumnIdFromIndex(m);
            this.deselectCell(newRowId, newColId);
          }
        }
      }
      this.selectionRegion.anchorItem2 = item3;
    } finally {
      this.selectionRegion.resumeChangeNotifications();
    }
  }

  /**
   * "Friend" method called from DataGridSelectionMgr used to extend column selection.
   * @param {ColId} colId
   */
  extendColumnSelection(colId) {
    try {
      this.selectionRegion.suspendChangeNotifications();
      if (this.selectionRegion.anchorItem == null || !this.selectionRegion.anchorItem.isColumn || this.selectionRegion.anchorItem2 == null || !this.selectionRegion.anchorItem2.isColumn) {
        this.selectColumn(colId, false);
        return;
      }

      // Have to consider 6 regions!
      let index1 = this.dataGrid.getColumnIndexFromId(this.selectionRegion.anchorItem.colId);
      let index2 = this.dataGrid.getColumnIndexFromId(this.selectionRegion.anchorItem2.colId);
      let item3 = this.getEffectiveItemForColumn(colId, index1, index2);
      if (item3 == null) {
        // Just select the column and be done with this
        this.selectColumn(colId, false);
        return;
      }
      const index3 = this.dataGrid.getColumnIndexFromId(item3.colId);
      if (this.selectionRegion.anchorItemGroup) {
        // Possibly reinterpret index1 which is left edge of the anchorItemGroup based on value of index3
        if (index3 < index1) {
          // We are extending to left so make index1 the right-most leaf column of the group
          let colGroup = this.dataGrid.getColumnFromId(this.selectionRegion.anchorItemGroup.colId);
          let leafCols = colGroup.getAllLeafColumns(true);
          if (leafCols.length > 0) {
            let lastLeafCol = leafCols[leafCols.length - 1];
            index1 = this.dataGrid.getColumnIndexFromId(lastLeafCol.id);
          }
        }
      }
      let s1 = -1;
      let s2 = -1;
      let u1 = -1;
      let u2 = -1;
      if (index1 <= index2) {
        if (index3 >= index2) {
          // Simple extend
          s1 = index2 + 1;
          s2 = index3;
        } else if (index3 > index1) {
          u1 = index3 + 1;
          u2 = index2;
        } else {
          u1 = index1 + 1;
          u2 = index2;
          s1 = index3;
          s2 = index1 - 1;
        }
      } else {
        // Inverted region where index2 < index1
        if (index3 <= index2) {
          s1 = index3;
          s2 = index2 - 1;
        } else if (index3 < index1) {
          u1 = index2;
          u2 = index3 - 1;
        } else {
          u1 = index2;
          u2 = index1 - 1;
          s1 = index1 + 1;
          s2 = index3;
        }
      }
      if (s1 >= 0) {
        for (let n = s1; n <= s2; n++) {
          let newColId = this.dataGrid.getColumnIdFromIndex(n);
          this.selectColumn(newColId, true);
        }
      }
      if (u1 >= 0) {
        for (let n = u1; n <= u2; n++) {
          let newColId = this.dataGrid.getColumnIdFromIndex(n);
          this.deselectColumn(newColId);
        }
      }
      this.selectionRegion.anchorItem2 = item3;
    } finally {
      this.selectionRegion.resumeChangeNotifications();
    }
  }

  /**
   * Called from extendColumnSelection(), this helper method attempts to deal with the sticky problem of determining
   * how to extend the selection if the user clicks on a group header. This method examines the current anchorItems and returns
   * a SelectedItem corresponding to one the edge leaf colums of colId. If the group spans both anchorItems, we return null
   * which will be interpreted by the caller.
   * @param {ColId} colId
   * @returns {SelectedItem | null}
   */
  getEffectiveItemForColumn(colId, index1, index2) {
    let col = this.dataGrid.getColumnFromId(colId);
    if (col == null) {
      return null;
    }
    if (col.isLeafColumn) {
      return new SelectedItem(null, colId);
    }
    let leafCols = col.getAllLeafColumns(true);
    let leftColId = leafCols[0].id;
    let index3Left = this.dataGrid.getColumnIndexFromId(leftColId);
    let rightColId = leafCols[leafCols.length - 1].id;
    let index3Right = this.dataGrid.getColumnIndexFromId(rightColId);
    let isRight = index3Right >= Math.max(index1, index2);
    let isLeft = index3Left <= Math.min(index1, index2);
    if (isRight === isLeft) {
      // Region either spans current selection, or is completely contained in current selection
      return null;
    }
    if (isRight) {
      return new SelectedItem(null, rightColId);
    }
    if (isLeft) {
      return new SelectedItem(null, leftColId);
    }
    return null; // make compiler happy
  }

  /**
   * Removes all selected items that refer to the column ids passed in.
   * Note that this affects any column and cell selections.
   * @param {ColId[]} ids Array of column ids to process.
   */
  removeSelectedItemsForColumns(ids) {
    if (this.selectionRegion.isSelectAll) {
      // This state is an optimization and we don't have to worry about any kind of walking
      return;
    }
    this.selectionRegion.suspendChangeNotifications();
    try {
      let haveSelectedItemsToProcess = false;
      for (let id of ids) {
        this.selectionRegion.deselectColumn(id);
        if (this.selectionRegion.columnHasSelectedCells(id)) {
          haveSelectedItemsToProcess = true;
        }
      }
      if (!haveSelectedItemsToProcess) {
        // During our column walk, we determined that none of the removedColumns have any selected cells in them
        // so we are done
        return;
      }

      // Now process all selected items and remove any that refer to the ColIds in the removedColumns
      let idSet = new Set(ids);
      let selectedItems = this.selectionRegion.getSelectedItems();
      for (let selectedItem of selectedItems) {
        // We only have to worry about cells
        if (selectedItem.isCell && idSet.has(selectedItem.colId)) {
          this.selectionRegion.deselectCell(selectedItem.rowId, selectedItem.colId);
        }
      }
    } finally {
      this.selectionRegion.resumeChangeNotifications();
    }
  }

  /**
   * Called when the DataGrid props change, this method checks for any columns that may have been removed
   * and cleans up any selected items that refer to these removed columns. See CAT-920 for more details.
   * @param {DataGridColumn[]} columns Current columns
   * @param {DataGridColumn[]} nextColumns Next columns via props.
   */
  updateSelectionWithNewProps(columns, nextColumns) {
    if (this.selectionRegion.isSelectAll) {
      // This state is an optimization and we don't have to worry about any kind of walking
      return;
    }
    let flattenedColumns = DataGridColumn.getFlattenedColumns(columns);
    let nextFlattenedColumns = DataGridColumn.getFlattenedColumns(nextColumns);
    let newSet = new Set();
    for (let col of nextFlattenedColumns) {
      newSet.add(col.id);
    }
    let removedColIds = [];
    for (let col of flattenedColumns) {
      if (!newSet.has(col.id)) {
        removedColIds.push(col.id);
      }
    }
    if (removedColIds.length === 0) {
      return;
    }
    this.removeSelectedItemsForColumns(removedColIds);
  }
}

// Defines the type for row key. For flat data, number is the 0-based row index,
// for hierarchical data, should be a string representing a row ID.
// If null, we are referring to a column.
_defineProperty(DataGridSelectionMachine, "LOG", LogUtils.getLogger(__filename)); // Defines the type for a column key. It is a string referring to the DataGridColumn.id.
// If null, we are referring to a row.
export let SelectionMode = /*#__PURE__*/function (SelectionMode) {
  SelectionMode[SelectionMode["None"] = 0] = "None";
  SelectionMode[SelectionMode["SingleRow"] = 1] = "SingleRow";
  SelectionMode[SelectionMode["MultipleRows"] = 2] = "MultipleRows";
  SelectionMode[SelectionMode["SingleCell"] = 3] = "SingleCell";
  SelectionMode[SelectionMode["MultipleCells"] = 4] = "MultipleCells";
  SelectionMode[SelectionMode["RectangularCells"] = 5] = "RectangularCells";
  return SelectionMode;
}({});

/**
 * Represents a selected item in the grid.
 * This may be a row, a cell or a column.
 */
export class SelectedItem {
  constructor(rowId = null, colId = null) {
    /**
     *   Identifier for the row.
     *   If it is a number, we have rectangular data and this represents a row index.
     *   If it is a string, we have hierarchical data and this represents the row id.
     *   If null, this represents a column selection.
     */
    _defineProperty(this, "rowId", void 0);
    /**
     * Identifier for a column. This is the DataGridColumn.id.
     * If null, this represents a row selection.
     */
    _defineProperty(this, "colId", void 0);
    this.rowId = rowId;
    this.colId = colId;
  }
  static areEqual(item1, item2) {
    if (item1 == null && item2 == null) {
      return true;
    }
    if (item1 == null) {
      return false;
    }
    if (item2 == null) {
      return false;
    }
    return item1.rowId === item2.rowId && item1.colId === item2.colId;
  }
  get isRow() {
    return this.rowId != null && this.colId == null;
  }
  isRowTypeGuard() {
    return this.rowId != null && this.colId == null;
  }
  get isCell() {
    return this.rowId != null && this.colId != null;
  }
  isCellTypeGuard() {
    return this.rowId != null && this.colId != null;
  }
  get isColumn() {
    return this.rowId == null && this.colId != null;
  }
  isColumnTypeGuard() {
    return this.rowId == null && this.colId != null;
  }
  areEqual(item) {
    return SelectedItem.areEqual(this, item);
  }
}
/**
 * Represents a rectangular region of selected data items.
 * See usage of this in DataGridSelectionMachine.getSelectionRange().
 */
export class SelectionRange {
  constructor(rowItems, columns, rows) {
    _defineProperty(this, "_rowItems", void 0);
    _defineProperty(this, "_columns", void 0);
    _defineProperty(this, "_rows", void 0);
    this._rowItems = rowItems;
    this._columns = columns;
    if (rows) {
      this._rows = rows;
    }
  }

  /**
   * Gets rectangular array of cells.
   * The outer Array represents row-level items, and each inner Array
   * is the Array<CellValueT> in that row.
   * The columns Array are the column objects that are associated with each cell
   * in the inner Array.
   * @returns {CellValueT[][]}
   */
  get rowItems() {
    return this._rowItems;
  }

  /**
   * Array of DataGridColumns associated with each cell in the inner Array of rowItems.
   * colItems length will always be the same as the length of each inner Array.
   * @returns {ColumnT[]}
   */
  get columns() {
    return this._columns;
  }

  /**
   * Row objects associated with each outer array in rowItems.
   * Note that this may be undefined if the implementer of getSelectionRangeImpl() chooses not to supply it.
   * @returns {DataGridRowT[]}
   */
  get rows() {
    return this._rows;
  }
}
//# sourceMappingURL=DataGridSelectionMachine.js.map
